import { Link, useNavigate } from "react-router-dom";
import { Bot, LayoutGrid, List, Search, Square, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useTrail, useSpring, animated, to } from "@react-spring/web";
import { CreateAgentPanel } from "@/components/organisms/studio/CreateAgentPanel";
import { progressFromEvents } from "@/components/organisms/studio/AgentRunProgress";
import { useAgents, useAgentRuns, useDeleteAgent } from "@/hooks/studio-platform";
import { useAgentRunStore } from "@/stores/agentRunStore";
import type { AgentRecord, AgentRunRecord, RunStatusValue } from "@/services/studio/types";
import { formatRelative } from "@/lib/studio/utils";


function durationLabel(ms: number | null | undefined): string {
  if (!ms || !Number.isFinite(ms) || ms <= 0) return "";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ── Status dot ────────────────────────────────────────────────────────────────
function StatusDot({ status }: { status?: RunStatusValue | string | null }) {
  if (status === "running") {
    return (
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--success))]" />
      </span>
    );
  }
  if (status === "done" || status === "cached") {
    return <span className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--success)/0.6)]" />;
  }
  if (status === "error" || status === "stopped") {
    return <span className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--destructive)/0.7)]" />;
  }
  return <span className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/20" />;
}

// ── Page ──────────────────────────────────────────────────────────────────────
export function AgentsList() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [showRecipes, setShowRecipes] = useState<boolean>(() => {
    // default: list view. workspace = "workspace" stored in localStorage.
    return localStorage.getItem("agents-view-preference") !== "workspace";
  });
  const [panelOpen, setPanelOpen] = useState(false);

  const toggleView = () => {
    setShowRecipes((prev) => {
      const next = !prev;
      localStorage.setItem("agents-view-preference", next ? "list" : "workspace");
      return next;
    });
  };

  const agentsQ = useAgents();
  const runsQ = useAgentRuns({ limit: 100 });

  const agents = agentsQ.data?.agents ?? [];

  // Build agentId → most-recent run map. Lookups use agent.id, which is the
  // SLUG (normalizeAgentRecord sets id = slug), while run.agent_id is the
  // backend UUID — key by the run's slug snapshot or nothing ever matches.
  const runsByAgentId = useMemo<Map<string, AgentRunRecord>>(() => {
    const map = new Map<string, AgentRunRecord>();
    const runs = runsQ.data?.runs ?? [];
    // runs are returned newest-first from the API
    for (const run of runs) {
      const key = run.surface_slug || run.agent_id;
      if (key && !map.has(key)) map.set(key, run);
    }
    return map;
  }, [runsQ.data?.runs]);

  // Poll every 3s while any agent is actively running
  const hasRunning = useMemo(
    () => [...runsByAgentId.values()].some((r) => r.status === "running"),
    [runsByAgentId],
  );
  useEffect(() => {
    if (!hasRunning) return;
    const id = setInterval(() => { void runsQ.refetch(); }, 3000);
    return () => clearInterval(id);
  }, [hasRunning]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((agent) =>
      agent.name.toLowerCase().includes(q) ||
      agent.description.toLowerCase().includes(q) ||
      agent.goal.toLowerCase().includes(q) ||
      agent.tools.some((t) => t.toLowerCase().includes(q))
    );
  }, [agents, query]);

  // Row entrance trail (real rows + skeleton rows — same config so they feel identical)
  const trail = useTrail(filtered.length, {
    from: { opacity: 0, transform: "translateY(6px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 340, friction: 28 },
  });

  const skeletonTrail = useTrail(SKELETON_WIDTHS.length, {
    from: { opacity: 0, transform: "translateY(6px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 340, friction: 28 },
  });

  return (
    <div className="flex h-full flex-col">
      {/* ── Sticky header — never scrolls ─────────────────────────────────── */}
      <div className="shrink-0 pb-3">
        {/* Title row */}
        <div className="flex items-end justify-between gap-4 py-1 pb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Agents</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Your intelligent agents — configure, run, and track results.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            className="studio-button studio-button-primary shrink-0"
          >
            <Bot className="h-3.5 w-3.5" />
            Create Agent
          </button>
        </div>

        {/* Search + view toggle */}
        <div className="flex items-center gap-2.5 rounded-lg border border-border/70 bg-muted/20 px-3 py-2 transition-colors focus-within:border-primary/40 focus-within:bg-muted/30">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, goal or tool…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/40"
          />
          {agents.length > 0 && !query && (
            <span className="shrink-0 font-mono text-[11px] text-muted-foreground/50">
              {agents.length} agents
            </span>
          )}
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="shrink-0 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
            >
              {filtered.length} found · clear
            </button>
          )}
          <span className="mx-0.5 h-4 w-px shrink-0 bg-border/60" />
          <HoverTooltip label={showRecipes ? "Workspace view" : "List view"}>
            <button
              type="button"
              onClick={toggleView}
              className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
            >
              {showRecipes ? <LayoutGrid className="h-3.5 w-3.5" /> : <List className="h-3.5 w-3.5" />}
            </button>
          </HoverTooltip>
        </div>
      </div>

      {/* ── Scrollable content ─────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {!showRecipes ? (
          /* Card grid — same filtered agents, different layout */
          agentsQ.isLoading ? (
            <AgentCardGridSkeleton />
          ) : filtered.length === 0 ? null : (
            <AgentCardGrid agents={filtered} runsByAgentId={runsByAgentId} />
          )
        ) : agentsQ.isLoading ? (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            {skeletonTrail.map((style, i) => (
              <animated.div key={i} style={style}>
                <SkeletonRow
                  nameWidth={SKELETON_WIDTHS[i].name}
                  metaWidth={SKELETON_WIDTHS[i].meta}
                  isLast={i === SKELETON_WIDTHS.length - 1}
                />
              </animated.div>
            ))}
          </div>
        ) : agentsQ.isError ? (
          <p className="font-mono text-sm text-destructive">
            {(agentsQ.error as Error)?.message ?? "Could not load agents."}
          </p>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-24 text-center">
            <Bot className="mb-4 h-12 w-12 text-muted-foreground/20" />
            <p className="font-semibold">No agents found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {query
                ? "Try a different search or clear the filter."
                : "Create your first agent to get started."}
            </p>
            {!query && (
              <Link to="/agents/new" className="studio-button studio-button-primary mt-5">
                Create Agent
              </Link>
            )}
          </div>
        ) : (
          /* Agent list — one rounded container, rows separated by hairline */
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            {trail.map((style, idx) => {
              const agent = filtered[idx];
              const latestRun = runsByAgentId.get(agent.id);
              return (
                <animated.div key={agent.id} style={style}>
                  <AgentRow
                    agent={agent}
                    latestRun={latestRun}
                    isLast={idx === filtered.length - 1}
                  />
                </animated.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Agent slide-in panel */}
      <CreateAgentPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onCreated={(agentId) => {
          setPanelOpen(false);
          void agentsQ.refetch();
          navigate(`/agents/${encodeURIComponent(agentId)}`);
        }}
      />
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
// Widths vary per row so placeholder text feels natural, not stamped
const SKELETON_WIDTHS = [
  { name: "w-52", meta: "w-32" },
  { name: "w-40", meta: "w-24" },
  { name: "w-64", meta: "w-36" },
  { name: "w-44", meta: "w-28" },
  { name: "w-56", meta: "w-32" },
  { name: "w-48", meta: "w-20" },
] as const;

function SkeletonRow({ nameWidth, metaWidth, isLast }: { nameWidth: string; metaWidth: string; isLast: boolean }) {
  return (
    <div
      className={[
        "flex items-center gap-4 px-5 py-3.5",
        !isLast ? "border-b border-border/60" : "",
      ].join(" ")}
    >
      {/* Status dot */}
      <div className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/15" />

      {/* Name + meta */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className={["h-3.5 rounded-md bg-muted-foreground/10 loading-parallax", nameWidth].join(" ")} />
        <div className={["h-2.5 rounded-md bg-muted-foreground/[0.07] loading-parallax", metaWidth].join(" ")} />
      </div>

      {/* Status column */}
      <div className="hidden sm:flex w-36 shrink-0 justify-end">
        <div className="h-2.5 w-24 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
      </div>
    </div>
  );
}

// ── Agent Row ─────────────────────────────────────────────────────────────────
function AgentRow({
  agent,
  latestRun,
  isLast,
}: {
  agent: AgentRecord;
  latestRun: AgentRunRecord | undefined;
  isLast: boolean;
}) {
  // Subscribe only to this agent's run entry — no re-renders for other agents
  const runEntry = useAgentRunStore((s) => s.runs.get(agent.id));
  const isActivelyRunning = runEntry?.phase === "running";
  const progress = isActivelyRunning
    ? progressFromEvents(runEntry?.events ?? []).percent
    : 0;
  const deleteAgent = useDeleteAgent();

  const displayRunning = isActivelyRunning || latestRun?.status === "running";

  return (
    <div
      className={[
        "relative group flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-muted/25",
        displayRunning ? "bg-[hsl(var(--success)/0.03)]" : "",
        !isLast ? "border-b border-border/60" : "",
      ].join(" ")}
    >
      {/* Status dot */}
      <StatusDot status={displayRunning ? "running" : latestRun?.status} />

      {/* Name + meta — expands */}
      <Link
        to={`/agents/${encodeURIComponent(agent.id)}`}
        className="min-w-0 flex-1 focus:outline-none"
      >
        <div className="flex items-baseline gap-2">
          <span className="truncate font-semibold text-sm text-foreground group-hover:text-primary transition-colors">
            {agent.name}
          </span>
          {agent.is_default && (
            <span className="shrink-0 font-mono text-[10px] text-muted-foreground/50">default</span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 font-mono text-[11px] text-muted-foreground">
          <span>{agent.tools.length} tools</span>
        </div>
      </Link>

      {/* Last run status — hidden while actively running (progress bar takes over) */}
      {!isActivelyRunning && (
        <div className="w-36 shrink-0 overflow-hidden text-right">
          <RunStatus run={latestRun} />
        </div>
      )}

      {/* Delete — only for user-created agents (default/global agents are protected) */}
      {!agent.is_default && !isActivelyRunning && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            if (window.confirm(`Delete agent "${agent.name}"? This removes the agent and its run history and cannot be undone.`)) {
              deleteAgent.mutate(agent.id);
            }
          }}
          disabled={deleteAgent.isPending}
          title="Delete this agent"
          className="shrink-0 rounded-md border border-border p-1.5 text-muted-foreground transition-colors hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Stop button — only visible while this agent is actively running */}
      {isActivelyRunning && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            useAgentRunStore.getState().stopRun(agent.id);
          }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-destructive/50 px-2.5 py-1 font-mono text-[11px] text-destructive transition-colors hover:bg-destructive/10"
        >
          <Square className="h-2.5 w-2.5" />
          Stop
        </button>
      )}

      {/* Live progress bar — thin strip at the bottom of the row */}
      {isActivelyRunning && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden">
          <div
            className="h-full bg-[hsl(var(--success))] transition-all duration-500"
            style={{ width: `${Math.max(progress, 3)}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ── Inline run status label ───────────────────────────────────────────────────
function RunStatus({ run }: { run: AgentRunRecord | undefined }) {
  if (!run) return null;

  if (run.status === "running") {
    return (
      <span className="block truncate font-mono text-[11px] text-[hsl(var(--success))]">
        Running · {formatRelative(run.started_at)}
      </span>
    );
  }
  if (run.status === "done" || run.status === "cached") {
    const dur = durationLabel(run.total_ms);
    return (
      <span className="block truncate font-mono text-[11px] text-muted-foreground">
        {dur ? `${dur} · ` : ""}{formatRelative(run.started_at)}
      </span>
    );
  }
  if (run.status === "error") {
    return (
      <span className="block truncate font-mono text-[11px] text-[hsl(var(--destructive)/0.8)]">
        Error · {formatRelative(run.started_at)}
      </span>
    );
  }
  if (run.status === "stopped") {
    return (
      <span className="block truncate font-mono text-[11px] text-muted-foreground">
        Stopped · {formatRelative(run.started_at)}
      </span>
    );
  }
  return (
    <span className="block truncate font-mono text-[11px] text-muted-foreground">
      {run.status} · {formatRelative(run.started_at)}
    </span>
  );
}

// ── Agent card grid (workspace view) ─────────────────────────────────────────
function AgentCardGrid({
  agents,
  runsByAgentId,
}: {
  agents: AgentRecord[];
  runsByAgentId: Map<string, AgentRunRecord>;
}) {
  const trail = useTrail(agents.length, {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 300, friction: 28 },
  });

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {trail.map((style, idx) => (
        <animated.div key={agents[idx].id} style={style} className="h-full">
          <AgentCard agent={agents[idx]} latestRun={runsByAgentId.get(agents[idx].id)} />
        </animated.div>
      ))}
    </div>
  );
}

function AgentCard({ agent, latestRun }: { agent: AgentRecord; latestRun: AgentRunRecord | undefined }) {
  const runEntry = useAgentRunStore((s) => s.runs.get(agent.id));
  const isActivelyRunning = runEntry?.phase === "running";
  const progress = isActivelyRunning
    ? progressFromEvents(runEntry?.events ?? []).percent
    : 0;
  const deleteAgent = useDeleteAgent();

  const displayRunning = isActivelyRunning || latestRun?.status === "running";

  const [springs, api] = useSpring(() => ({
    shadow: 0,
    config: { tension: 300, friction: 26 },
  }));

  return (
    <animated.div
      style={{
        boxShadow: springs.shadow.to(
          (s) => `inset 0 1px 0 rgba(255,255,255,0.04), 0 ${1 + s * 8}px ${3 + s * 18}px rgba(0,0,0,${0.05 + s * 0.09})`
        ),
      }}
      onMouseEnter={() => api.start({ shadow: 1 })}
      onMouseLeave={() => api.start({ shadow: 0 })}
      className={[
        "studio-card group relative flex h-full flex-col p-4",
        displayRunning ? "bg-[hsl(var(--success)/0.03)]" : "",
      ].join(" ")}
    >
      <Link to={`/agents/${encodeURIComponent(agent.id)}`} className="flex min-w-0 flex-col gap-2.5 focus:outline-none">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <StatusDot status={displayRunning ? "running" : latestRun?.status} />
            <span className="truncate font-semibold text-sm leading-snug text-foreground hover:text-primary transition-colors">
              {agent.name}
            </span>
            {agent.is_default && (
              <span className="shrink-0 font-mono text-[10px] text-muted-foreground/50">default</span>
            )}
          </div>
        </div>

        {/* Description */}
        {agent.description && (
          <p className="line-clamp-2 text-xs text-muted-foreground leading-relaxed">
            {agent.description}
          </p>
        )}

        {/* Footer metrics */}
        <div className="mt-auto flex items-center gap-3 font-mono text-[10px] text-muted-foreground">
          <span>{agent.tools.length} tools</span>
          <span className="opacity-30">·</span>
          <span>{agent.plan.length} steps</span>
          {latestRun && !isActivelyRunning && latestRun.status !== "running" && (
            <>
              <span className="opacity-30">·</span>
              <span>{formatRelative(latestRun.started_at)}</span>
            </>
          )}
        </div>
      </Link>

      {/* Stop button — absolute overlay, no height impact */}
      {isActivelyRunning && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            useAgentRunStore.getState().stopRun(agent.id);
          }}
          className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-md border border-destructive/50 bg-card px-2 py-0.5 font-mono text-[10px] text-destructive transition-colors hover:bg-destructive/10"
        >
          <Square className="h-2 w-2" />
          Stop
        </button>
      )}

      {/* Delete — top-right overlay, only for user-created agents */}
      {!agent.is_default && !isActivelyRunning && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            if (window.confirm(`Delete agent "${agent.name}"? This removes the agent and its run history and cannot be undone.`)) {
              deleteAgent.mutate(agent.id);
            }
          }}
          disabled={deleteAgent.isPending}
          title="Delete this agent"
          className="absolute right-2 top-2 rounded-md border border-border bg-card p-1.5 text-muted-foreground transition-colors hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Live progress bar at the bottom of the card */}
      {isActivelyRunning && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden rounded-b-xl">
          <div
            className="h-full bg-[hsl(var(--success))] transition-all duration-500"
            style={{ width: `${Math.max(progress, 3)}%` }}
          />
        </div>
      )}
    </animated.div>
  );
}

const CARD_SKELETON_WIDTHS = ["w-44", "w-36", "w-52", "w-40"] as const;

function AgentCardGridSkeleton() {
  const trail = useTrail(4, {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 300, friction: 28 },
  });
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {trail.map((style, idx) => (
        <animated.div key={idx} style={style} className="studio-card flex flex-col gap-2.5 p-4">
          {/* Header: dot + name */}
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/15" />
            <div className={["h-3.5 rounded-md bg-muted-foreground/10 loading-parallax", CARD_SKELETON_WIDTHS[idx]].join(" ")} />
          </div>

          {/* Description: two lines */}
          <div className="space-y-1.5">
            <div className="h-2.5 w-full rounded bg-muted-foreground/[0.07] loading-parallax" />
            <div className="h-2.5 w-3/4 rounded bg-muted-foreground/[0.05] loading-parallax" />
          </div>

          {/* Metrics row: dots between items */}
          <div className="mt-auto flex items-center gap-2">
            <div className="h-2 w-12 rounded bg-muted-foreground/[0.07] loading-parallax" />
            <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/10" />
            <div className="h-2 w-10 rounded bg-muted-foreground/[0.07] loading-parallax" />
            <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/10" />
            <div className="h-2 w-14 rounded bg-muted-foreground/[0.07] loading-parallax" />
          </div>
        </animated.div>
      ))}
    </div>
  );
}

// ── HoverTooltip — same spring pattern as DockTooltip in org360/GraphDock ────
function HoverTooltip({ label, children }: { label: string; children: ReactNode }) {
  const [visible, setVisible] = useState(false);
  const springs = useSpring({
    opacity: visible ? 1 : 0,
    y: visible ? 0 : -4,
    scale: visible ? 1 : 0.9,
    config: { tension: 420, friction: 24 },
  });
  return (
    <div
      className="relative"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      <animated.div
        className="pointer-events-none absolute top-full right-0 z-50 mt-2"
        style={{
          transform: to(
            [springs.y, springs.scale],
            (y, s) => `translateY(${y}px) scale(${s})`,
          ),
          opacity: springs.opacity,
        }}
      >
        <div className="whitespace-nowrap rounded-lg bg-foreground/90 px-2.5 py-1 font-mono text-[11px] font-medium text-background shadow-[0_2px_8px_hsl(var(--foreground)/0.15)]">
          {label}
        </div>
      </animated.div>
    </div>
  );
}
